import { useTranslation } from 'react-i18next';

import { Font, View } from '@react-pdf/renderer';
import { IBusinessToolType } from 'constants/businessPlan';
import { DevelopmentTimelineTable } from 'PDF/components/DevelopmentTimeline/DevelopmentTimelineTable';
import { PDFPage } from 'PDF/components/Page';
import { ITextItem, PdfContentText } from 'PDF/components/PdfContentText';
import { ProductsServicesDescription } from 'PDF/components/ProductsServicesDescription';
import {
  ICertifications,
  ICharacteristic,
} from 'PDF/components/ProductsServicesDescription/ProductsServicesDescription';
import { SmallHeading } from 'PDF/components/SmallHeading';
import { SubChapterHeading } from 'PDF/components/SubChapterHeading';
import { PDF_SECTIONS } from 'PDF/constants/sections';
import { Noto } from 'PDF/fonts';
import { prepareSectionId } from 'PDF/helpers/pdfMapping';
import { getSectionInfo } from 'PDF/helpers/sections';
import {
  getProductAndServicesChapterNames,
  ProductsAndServicesKeys,
  ProductsAndServicesSectionAvailability,
} from 'PDF/types/pdfSections';
import { ITimelineItem } from 'PDF/types/timeline';

import { getSubSectionHeading } from 'pages/BusinessToolPage/PdfPreview/utils';

import { SectionCoverPagePdf } from '../SectionCoverPage';
import { getNextSectionIndex } from '../TableOfContents/helpers';

Font.getRegisteredFonts();
Font.register(Noto);

interface IProductAndServicesData {
  characteristics: ICharacteristic[];
  certifications: ICertifications[];
  developmentTimeline: ITimelineItem[];
  patentsOrTrademarks: ICertifications[];
}

export interface IProductsAndServicesPdfProps {
  data: IProductAndServicesData;
  magazineItems: ITextItem[][];
  businessPlanType: IBusinessToolType;
  sectionPicture?: string;
  isStandardOrLightTariff: boolean;
  isExamplePage: boolean;
}

export const ProductsAndServicesPdf = (
  props: IProductsAndServicesPdfProps & {
    availability: ProductsAndServicesSectionAvailability;
    isPreviewMode?: boolean;
    sectionNumber: string;
  },
) => {
  const {
    data,
    magazineItems,
    businessPlanType,
    sectionPicture,
    availability,
    isPreviewMode = false,
    sectionNumber,
    isStandardOrLightTariff,
    isExamplePage
  } = props;
  const { t } = useTranslation();
  const { sectionHeading } = getSectionInfo(businessPlanType, PDF_SECTIONS.PRODUCT_AND_SERVICES, isExamplePage);
  const getSubHeading = getSubSectionHeading(sectionNumber);
  const getSubChapterIndex = getNextSectionIndex();
  const PRODUCT_AND_SERVICES_CHAPTER_NAMES = getProductAndServicesChapterNames(isExamplePage);
  const subChapterNames = (Object.keys(availability) as ProductsAndServicesKeys[])
    .filter((key) => !!availability[key])
    .map((key) => `${Number(sectionNumber)}.${getSubChapterIndex()}. ${PRODUCT_AND_SERVICES_CHAPTER_NAMES[key]}`);
  const isSectionEmpty = Object.values(availability).every((value) => !value);
  const isCoverPageEmpty = isSectionEmpty && isPreviewMode;

  if (!data) {
    return (
      <SectionCoverPagePdf
        header={sectionHeading}
        sectionNumber={sectionNumber}
        sectionId={prepareSectionId(businessPlanType, PDF_SECTIONS.PRODUCT_AND_SERVICES)}
        background={sectionPicture ?? ''}
        highlightSettings={{ position: 0, variant: 'before' }}
        subChapterNames={subChapterNames}
        isEmpty={isCoverPageEmpty}
        isStandardOrLightTariff={isStandardOrLightTariff}
        isExamplePage={isExamplePage}
      />
    );
  }

  const { characteristics, certifications, developmentTimeline, patentsOrTrademarks } = data;

  return (
    <>
      {(!isSectionEmpty || isCoverPageEmpty) && (
        <SectionCoverPagePdf
          header={sectionHeading}
          sectionNumber={sectionNumber}
          sectionId={prepareSectionId(businessPlanType, PDF_SECTIONS.PRODUCT_AND_SERVICES)}
          background={sectionPicture ?? ''}
          highlightSettings={{ position: 0, variant: 'before' }}
          subChapterNames={subChapterNames}
          isEmpty={isCoverPageEmpty}
          isStandardOrLightTariff={isStandardOrLightTariff}
          isExamplePage={isExamplePage}
        />
      )}
      {!isSectionEmpty && (
        <PDFPage
          shouldShowHeaderAndFooter
          sectionNumber={sectionNumber}
          sectionTitle={sectionHeading}
          withEmptySpaceImage
          isStandardOrLightTariff={isStandardOrLightTariff}
        >
          {availability.description && (
            <ProductsServicesDescription
              heading={getSubHeading(PRODUCT_AND_SERVICES_CHAPTER_NAMES.description)}
              id="description"
              magazineItems={[magazineItems[0], magazineItems[1]]}
              certifications={certifications}
              characteristics={characteristics}
              patentsOrTrademarks={patentsOrTrademarks}
              isExamplePage={isExamplePage}
            />
          )}
          {availability.advantages && (
            <>
              <SubChapterHeading
                heading={getSubHeading(PRODUCT_AND_SERVICES_CHAPTER_NAMES.advantages)}
                id="advantages"
              />
              <PdfContentText items={magazineItems[2]} />
            </>
          )}
          {availability.rAndD && (
            <>
              <SubChapterHeading heading={getSubHeading(PRODUCT_AND_SERVICES_CHAPTER_NAMES.rAndD)} id="rAndD" />
              <PdfContentText items={magazineItems[3]} withoutBottomMargin={developmentTimeline.length === 0} />
              {developmentTimeline.length > 0 && (
                <View wrap={false}>
                  <SmallHeading heading={isExamplePage ? "Development Timeline" : t('pdfPages.mappings.marketingAndSales.devTimeline')} />
                  <DevelopmentTimelineTable items={developmentTimeline} isExamplePage={isExamplePage}/>
                </View>
              )}
              <PdfContentText items={magazineItems[4]} />
            </>
          )}
          {availability.deliveryAndSupport && (
            <>
              <SubChapterHeading
                heading={getSubHeading(PRODUCT_AND_SERVICES_CHAPTER_NAMES.deliveryAndSupport)}
                id="deliveryAndSupport"
              />
              <PdfContentText items={magazineItems[5]} />
            </>
          )}
        </PDFPage>
      )}
    </>
  );
};
